import { useState } from 'react'
import { Button, Card, IconButton, Input, Person } from '../../../design/components'
import { useDebounced } from '../../../lib/useDebounced'
import { useCharacterList } from '../../characters/queries'
import type { CampaignCharactersSection } from '../types'
import type { CampaignEditing } from '../useCampaignEditing'
import styles from './sections.module.css'

/**
 * The characters in play. Section `characters`.
 *
 * `Temp` characters — the GM's scratch NPCs — are filtered out for players
 * server-side, so a shorter list here is the rule working, not a bug. The client
 * renders what it is given and never tries to tell "hidden" from "none".
 */
export function RosterCard({
  characters,
  editing,
}: {
  characters: CampaignCharactersSection | undefined
  editing: CampaignEditing
}) {
  const [adding, setAdding] = useState(false)

  if (characters === undefined) return null

  const roster = characters.characters ?? []
  const canRemove = editing.access.isGameMaster

  return (
    <Card
      variant="skills"
      label="Roster"
      hint={`${roster.length} in play`}
      action={
        // `canAddCharacter` is the server's answer, not a rule re-implemented here:
        // a GM may always attach one, a player only while they have no PC in this
        // campaign. Hiding the control on anything else would be guessing.
        editing.access.canAddCharacter && !adding ? (
          <IconButton label="Add a character" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {roster.length === 0 && <p className={styles.empty}>No characters in this campaign yet.</p>}

      {roster.map((character) => (
        <div key={character.characterId} className={styles.row}>
          <div className={styles.rowHead}>
            <Person
              name={character.name}
              portraitUrl={character.portraitUrl}
              meta={[String(character.type), character.ownerName].filter(Boolean).join(' · ')}
              characterId={character.characterId}
            />
            {canRemove && (
              <Button
                variant="danger"
                disabled={editing.removeCharacter.isPending}
                onClick={() =>
                  editing.removeCharacter.mutate({ characterId: character.characterId })
                }
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      ))}

      {adding && (
        <AddCharacterForm
          editing={editing}
          alreadyIn={roster.map((character) => character.characterId)}
          onClose={() => setAdding(false)}
        />
      )}
    </Card>
  )
}

/**
 * Picks from the caller's own characters.
 *
 * Only the caller's, and that is the API's rule rather than a convenience: the roster
 * endpoint 404s a character the caller does not own — even for the Game Master, who
 * attaches their own NPCs and GM-PCs. So the picker is the character roster, which
 * `useCharacterList` already fetches and filters server-side.
 */
function AddCharacterForm({
  editing,
  alreadyIn,
  onClose,
}: {
  editing: CampaignEditing
  alreadyIn: readonly number[]
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search)
  const mine = useCharacterList(1, 50, debounced)

  const candidates = (mine.data?.items ?? []).filter(
    (character) => !alreadyIn.includes(character.id ?? 0),
  )

  return (
    <div className={styles.inlineForm}>
      <Input
        label="Your characters"
        type="search"
        placeholder="Filter by name…"
        autoFocus
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {mine.isPending && <p className={styles.note}>Loading…</p>}

      {mine.isSuccess && candidates.length === 0 && (
        <p className={styles.note}>
          {search.trim() === ''
            ? 'Every character you own is already in this campaign.'
            : 'No character of yours matches that.'}
        </p>
      )}

      <div className={styles.pickerList}>
        {candidates.map((character) => (
          <button
            key={character.id}
            type="button"
            className={styles.pickerRow}
            disabled={editing.addCharacter.isPending}
            onClick={() =>
              editing.addCharacter.mutate(
                { characterId: character.id ?? 0 },
                { onSuccess: onClose },
              )
            }
          >
            <Person
              name={character.name ?? ''}
              portraitUrl={character.portraitUrl}
              meta={String(character.type ?? '')}
            />
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
